/**
 * Demo data for the assistant (?mock=1): settings, four conversations (an answer with reads,
 * one waiting on an approval with a failed step, a spend summary, and one that failed), and
 * the demo answers to what a person does on the page. Nothing here runs a model.
 */
import { ASSISTANT_DEFAULT_SETTINGS, conversationTitle, summarizeConversation } from '@orgus/protocol';
import type { AssistantEntry, AssistantSettings, AssistantState, AssistantStep, Conversation, Id } from '@orgus/protocol';

const S = 1000;
const M = 60 * S;
const H = 60 * M;

export interface MockMachine {
  id: Id;
  name: string;
}

let seq = 0;
const nextId = (p: string) => `${p}_demo${(++seq).toString(36)}`;

function step(s: Partial<AssistantStep> & Pick<AssistantStep, 'capability' | 'title' | 'tier' | 'outcome'>): AssistantStep {
  // As the hub keeps them: the tool as Claude Code named it, and its tool_use id (subagent steps point at it).
  const tool = s.capability.startsWith('tool:') ? s.capability.slice(5) : `mcp__orgus__${s.capability.replace(/\./g, '_')}`;
  return { input: {}, message: null, approval: null, auditId: null, ms: null, tool, toolUseId: nextId('tu'), ...s };
}

function conv(c: Partial<Conversation> & Pick<Conversation, 'id' | 'title' | 'state' | 'entries' | 'createdAt' | 'updatedAt'>): Conversation {
  return { by: 'you', role: 'owner', runId: null, machineId: null, model: 'sonnet', claudeSessionId: null, cost: 0, turns: 0, error: null, ...c };
}

/** The spend demo's task list at one point in the reply. */
function spendPlan(status: string[]): AssistantStep {
  const titles = [
    ['Read spend by project', 'Reading spend'],
    ['Find what drove the top project', 'Finding the driver'],
    ['Write the summary', 'Writing the summary'],
  ];
  return step({ capability: 'tool:TodoWrite', title: 'Keep a task list', tier: 'read', outcome: 'ok', ms: 10, input: { todos: titles.map(([content, activeForm], i) => ({ content, activeForm, status: status[i] })) } });
}

/** The demo conversations, newest first. */
export function mockConversations(now: number, machines: readonly MockMachine[]): Conversation[] {
  const m1 = machines[0]?.id ?? null;
  const m2 = machines[1]?.id ?? m1;
  const busyName = machines[1]?.name ?? machines[0]?.name ?? 'build-02';
  const e = (id: string, at: number, rest: Omit<AssistantEntry, 'id' | 'at'>): AssistantEntry => ({ id, at: now - at, ...rest });

  const waiting = conv({
    id: 'asc_demo_idle',
    title: 'Stop every idle session older than a day',
    state: 'waiting',
    runId: 'run_demo_idle',
    machineId: m1,
    model: 'sonnet',
    claudeSessionId: 'c1b7e0d2',
    createdAt: now - 6 * M,
    updatedAt: now - 40 * S,
    cost: 0.18,
    turns: 6,
    entries: [
      e('ase_1', 6 * M, { kind: 'user', by: 'you', text: 'Stop every idle session older than a day' }),
      e('ase_2', 6 * M - 4 * S, {
        kind: 'step',
        step: step({ capability: 'sessions.list', title: 'List sessions', tier: 'read', outcome: 'ok', input: { status: 'idle', limit: 50 }, message: 'Found 3 idle sessions older than a day.', ms: 140 }),
      }),
      e('ase_3', 6 * M - 10 * S, {
        kind: 'text',
        text: 'I found **3 idle sessions** older than a day:\n\n- `7f3a` Fix the login race, idle for 2 days\n- `91c2` Spike: cache warmup, idle for 26 hours\n- `b44e` Docs pass on the runbooks, idle for 3 days\n\nStopping them now. They have no uncommitted work.',
      }),
      e('ase_4', 5 * M, {
        kind: 'step',
        step: step({ capability: 'session.stop', title: 'Stop a session', tier: 'operate', outcome: 'ok', input: { sessionId: '7f3a', reason: 'Idle for 2 days' }, message: 'Stopped session 7f3a.', auditId: 'au_demo71', ms: 1800 }),
      }),
      e('ase_5', 5 * M - 20 * S, {
        kind: 'step',
        step: step({ capability: 'session.stop', title: 'Stop a session', tier: 'operate', outcome: 'ok', input: { sessionId: 'b44e', reason: 'Idle for 3 days' }, message: 'Stopped session b44e.', auditId: 'au_demo72', ms: 1650 }),
      }),
      e('ase_6', 4 * M, {
        kind: 'step',
        step: step({
          capability: 'session.stop',
          title: 'Stop a session',
          tier: 'operate',
          outcome: 'error',
          input: { sessionId: '91c2', reason: 'Idle for 26 hours' },
          message: `Machine ${busyName} did not answer in 30 s. It may be asleep or offline.`,
          ms: 30_000,
        }),
      }),
      e('ase_7', 3 * M, {
        kind: 'text',
        text: `Session \`91c2\` did not stop: ${busyName} did not answer. Its worktree \`feat/cache-warmup\` has had no commits for 4 days, so I would like to remove it to free the disk. That removes work, so it waits for your approval.`,
      }),
      e('ase_8', 40 * S, {
        kind: 'step',
        step: step({
          capability: 'worktree.remove',
          title: 'Remove a worktree',
          tier: 'elevated',
          outcome: 'pending',
          input: { machine: busyName, path: 'C:/work/orgus-wt/feat-cache-warmup', force: false },
          message: 'Waiting for an owner or admin to approve.',
          approval: { id: 'apr_demo_wt', why: 'Run everyday changes asks before elevated changes.', decidedBy: null, decidedAt: null },
        }),
      }),
    ],
  });

  const needs = conv({
    id: 'asc_demo_needs',
    title: 'What needs me right now?',
    state: 'idle',
    machineId: m1,
    claudeSessionId: 'a3f9c8e1',
    createdAt: now - 52 * M,
    updatedAt: now - 49 * M,
    cost: 0.06,
    turns: 3,
    entries: [
      e('asn_1', 52 * M, { kind: 'user', by: 'you', text: 'What needs me right now?' }),
      e('asn_2', 52 * M - 3 * S, { kind: 'step', step: step({ capability: 'needs_you.list', title: 'What needs a person', tier: 'read', outcome: 'ok', input: {}, message: '4 items.', ms: 90 }) }),
      e('asn_3', 51 * M, {
        kind: 'text',
        text: 'Four things need you, most urgent first:\n\n1. **Approval** in *Held approvals*: a `git push` to `main` on studio.\n2. **Failed pipeline item** in Qawl-Agents: the landing check failed twice on `site-lists`.\n3. **Machine** devbox stopped reporting 40 minutes ago.\n4. **Worktree** `feat/old-menu` has uncommitted changes nobody touched for a day.\n\nSay the word and I can open the failed item or remove the worktree.',
      }),
    ],
  });

  const spend = conv({
    id: 'asc_demo_spend',
    title: "Summarise today's spend by project",
    state: 'idle',
    machineId: m2,
    model: 'haiku',
    claudeSessionId: 'f02d51b7',
    createdAt: now - 3 * H,
    updatedAt: now - 3 * H + 2 * M,
    cost: 0.03,
    turns: 2,
    replies: [
      {
        runId: 'run_demo_spend',
        startedAt: now - 3 * H,
        endedAt: now - 3 * H + 38 * S,
        outcome: 'ok',
        model: 'claude-haiku-5-5',
        machineId: m2,
        cost: 0.03,
        durationMs: 38 * S,
        turns: 5,
        tokens: { input: 1_840, output: 920, cacheRead: 24_600, cacheWrite: 3_100 },
        context: { used: 27_400, size: 200_000 },
      },
    ],
    entries: [
      e('ass_1', 3 * H, { kind: 'user', by: 'you', text: "Summarise today's spend by project" }),
      e('ass_1t', 3 * H - 1 * S, { kind: 'thinking', runId: 'run_demo_spend', ms: 3_400, text: 'Read the cost summary first, then the sessions behind the biggest project.' }),
      e('ass_1p', 3 * H - 2 * S, {
        kind: 'step',
        runId: 'run_demo_spend',
        step: step({
          capability: 'tool:TodoWrite',
          title: 'Keep a task list',
          tier: 'read',
          outcome: 'ok',
          ms: 10,
          input: {
            todos: [
              { content: 'Read spend by project', activeForm: 'Reading spend', status: 'in_progress' },
              { content: 'Find what drove the top project', activeForm: 'Finding the driver', status: 'pending' },
              { content: 'Write the summary', activeForm: 'Writing the summary', status: 'pending' },
            ],
          },
        }),
      }),
      e('ass_2', 3 * H - 3 * S, {
        kind: 'step',
        runId: 'run_demo_spend',
        step: step({
          capability: 'costs.summary',
          title: 'Spend and budgets',
          tier: 'read',
          outcome: 'ok',
          input: { period: 'today', by: 'project' },
          ms: 210,
          result: JSON.stringify({
            total: 41.2,
            budget: 150,
            projects: [
              { project: 'Qawl-Agents', costUsd: 22.8, sessions: 6 },
              { project: 'orgus', costUsd: 12.1, sessions: 4 },
              { project: 'site-lists', costUsd: 4.9, sessions: 2 },
              { project: 'docs', costUsd: 1.4, sessions: 1 },
            ],
          }),
        }),
      }),
      e('ass_2p', 3 * H - 8 * S, { kind: 'step', runId: 'run_demo_spend', step: spendPlan(['completed', 'in_progress', 'pending']) }),
      e('ass_2b', 3 * H - 9 * S, {
        kind: 'step',
        runId: 'run_demo_spend',
        step: step({ capability: 'sessions.list', title: 'List sessions', tier: 'read', outcome: 'ok', input: { project: 'Qawl-Agents' }, ms: 160, result: JSON.stringify([{ id: 's1', title: 'Held approvals mission', state: 'working', costUsd: 14.2 }, { id: 's2', title: 'Landing queue fixes', state: 'idle', costUsd: 5.1 }, { id: 's3', title: 'Docs pass', state: 'idle', costUsd: 3.5 }]) }),
      }),
      e('ass_2q', 3 * H - 20 * S, { kind: 'step', runId: 'run_demo_spend', step: spendPlan(['completed', 'completed', 'completed']) }),
      e('ass_3', 3 * H - 2 * M, {
        kind: 'text',
        runId: 'run_demo_spend',
        text: "Today's spend is **$41.20** of a $150 budget.\n\n| Project | Spend | Sessions |\n|---|---:|---:|\n| Qawl-Agents | $22.80 | 6 |\n| orgus | $12.10 | 4 |\n| site-lists | $4.90 | 2 |\n| docs | $1.40 | 1 |\n\nMost of Qawl-Agents is the **held-approvals mission** ($14.20). Nothing is near its budget.",
      }),
    ],
  });

  const failed = conv({
    id: 'asc_demo_defect',
    title: 'Log a bug for the last failed landing',
    state: 'failed',
    machineId: m1,
    claudeSessionId: '7d1e2a90',
    createdAt: now - 26 * H,
    updatedAt: now - 26 * H + 4 * M,
    cost: 0.41,
    turns: 9,
    error: 'The reply stopped at its $0.40 budget.',
    entries: [
      e('asd_1', 26 * H, { kind: 'user', by: 'you', text: 'Log a bug for the last failed landing' }),
      e('asd_2', 26 * H - 3 * S, { kind: 'step', step: step({ capability: 'pipelines.list', title: 'List pipelines', tier: 'read', outcome: 'ok', input: { state: 'failed' }, message: '1 failed item in the last day.', ms: 120 }) }),
      e('asd_2b', 26 * H - 1 * M, {
        kind: 'step',
        step: step({
          capability: 'tool:Read',
          title: 'Read a file',
          tier: 'read',
          outcome: 'ok',
          input: { file_path: 'C:/work/site-lists/.orgus-dev/logs/landing-check.log' },
          message: 'Read the landing check log: 2 failing tests in checkout.spec.ts.',
          ms: 60,
        }),
      }),
      e('asd_3', 26 * H - 2 * M, {
        kind: 'step',
        step: step({
          capability: 'api.call',
          title: 'Change through any module API',
          tier: 'elevated',
          outcome: 'denied',
          input: { method: 'POST', path: '/api/missions/items', body: { kind: 'defect', title: 'Landing check fails on site-lists after rebase' } },
          message: 'Denied by you: file it under the site-lists epic instead.',
          approval: { id: 'apr_demo_old', why: 'Run everyday changes asks before elevated changes.', decidedBy: 'you', decidedAt: now - 26 * H + 3 * M },
        }),
      }),
      e('asd_4', 26 * H - 4 * M, { kind: 'error', text: 'The reply stopped at its $0.40 budget. Raise the budget per reply in Settings, then send the message again.' }),
    ],
  });

  // A machine without Claude Code: Orgus installs it there first (ADR 0107 amendment).
  const vm = machines[machines.length - 1]?.name ?? 'vm-eu-1';
  const installing = conv({
    id: 'asc_demo_install',
    title: 'Which landings failed overnight?',
    state: 'running',
    machineId: null,
    createdAt: now - 50 * S,
    updatedAt: now - 48 * S,
    installing: { machineId: machines[machines.length - 1]?.id ?? 'm_vm', name: vm, installId: 'inst_demo', since: now - 48 * S, until: now + 9 * M },
    entries: [
      e('asi_1', 50 * S, { kind: 'user', by: 'you', text: 'Which landings failed overnight?' }),
      e('asi_2', 48 * S, { kind: 'note', text: `Installing Claude Code on ${vm}. The reply starts when it is ready.` }),
    ],
  });

  // No account and a machine with no sign-in: the one step, with its button.
  const noAccount = conv({
    id: 'asc_demo_account',
    title: 'Summarise the open approvals',
    state: 'failed',
    machineId: m2,
    createdAt: now - 3 * 24 * H,
    updatedAt: now - 3 * 24 * H + 2 * S,
    error: `Claude Code on ${vm} is not signed in, and the workspace has no Claude account to run it on.`,
    entries: [
      e('asa_1', 3 * 24 * H, { kind: 'user', by: 'you', text: 'Summarise the open approvals' }),
      e('asa_2', 3 * 24 * H - 2 * S, {
        kind: 'error',
        code: 'no-account',
        text: `Claude Code on ${vm} is not signed in, and the workspace has no Claude account to run it on. Add an account in Settings, Agent accounts (a Claude subscription or an API key), then send the message again.`,
      }),
    ],
  });

  return [installing, waiting, needs, spend, failed, toolsDemo(now, m1, e), noAccount];
}

/** A reply that uses Claude Code's own tools, one of each kind, for the templates. */
function toolsDemo(now: number, machineId: Id | null, e: (id: string, at: number, rest: Omit<AssistantEntry, 'id' | 'at'>) => AssistantEntry): Conversation {
  const run = 'run_demo_tools';
  const task = step({ capability: 'tool:Task', title: 'Start a subagent', tier: 'read', outcome: 'ok', input: { description: 'Count the chat panel files', prompt: 'Count the .tsx files under features/assistant.' }, ms: 9_400, result: 'There are 9 .tsx files.' });
  const at = (sec: number) => 40 * M - sec * S;
  return conv({
    id: 'asc_demo_tools',
    title: 'Look through the chat panel code',
    state: 'idle',
    machineId,
    claudeSessionId: 'b8e2c410',
    createdAt: now - 40 * M,
    updatedAt: now - 38 * M,
    cost: 0.22,
    turns: 7,
    replies: [{ runId: run, startedAt: now - 40 * M, endedAt: now - 38 * M, outcome: 'ok', model: 'claude-opus-5-5', machineId, cost: 0.22, durationMs: 74 * S, turns: 7, tokens: { input: 5_200, output: 2_100, cacheRead: 88_000, cacheWrite: 9_400 }, context: { used: 61_000, size: 1_000_000 } }],
    entries: [
      e('ast_1', at(0), { kind: 'user', by: 'you', text: 'Look through the chat panel code and tell me what is there.' }),
      e('ast_2', at(2), { kind: 'thinking', runId: run, ms: 4_100, text: 'Start with the readme, then search the assistant module and list its views.' }),
      e('ast_3', at(3), { kind: 'text', runId: run, text: 'I will read the readme first, then search the code.' }),
      e('ast_3s', at(3), {
        kind: 'step',
        runId: run,
        step: step({
          capability: 'state.summary',
          title: 'Summary of everything',
          tier: 'read',
          outcome: 'ok',
          ms: 110,
          result: JSON.stringify({
            at: now - 40 * M,
            sessions: { working: 8, waiting: 1, idle: 16, ended: 52 },
            subagentsRunning: 7,
            workflowsRunning: 1,
            pipelines: { count: 1, inFlight: 15, failedLastDay: 10, paused: 0, down: 0 },
            machines: { online: 1, stale: 0, offline: 0, total: 1 },
            projects: 2,
            jobsRunning: 0,
            needsYou: 30,
            spend: { today: 893.75, month: 24_901.88, currency: 'USD' },
            approvalsPending: 2,
            missionsRunning: 0,
          }),
        }),
      }),
      e('ast_4', at(4), { kind: 'step', runId: run, step: step({ capability: 'tool:Read', title: 'Read a file', tier: 'read', outcome: 'ok', input: { file_path: 'C:/work/orgus/README.md', limit: 40 }, ms: 40, result: '     1→# Orgus\n     2→\n     3→A control room for AI-assisted software development.\n     4→\n     5→Run `pnpm start` to open it on port 4600.' }) }),
      e('ast_5', at(6), { kind: 'step', runId: run, step: step({ capability: 'tool:Grep', title: 'Search files', tier: 'read', outcome: 'ok', input: { pattern: 'onEvent', path: 'C:/work/orgus/apps/hub/src/ext/assistant' }, ms: 120, result: 'Found 2 files\nC:/work/orgus/apps/hub/src/ext/assistant/index.ts\nC:/work/orgus/apps/hub/src/ext/assistant/assistant.test.ts', structured: { mode: 'files_with_matches', filenames: ['C:/work/orgus/apps/hub/src/ext/assistant/index.ts', 'C:/work/orgus/apps/hub/src/ext/assistant/assistant.test.ts'], numFiles: 2 } }) }),
      e('ast_6', at(7), { kind: 'step', runId: run, step: step({ capability: 'tool:Glob', title: 'Find files', tier: 'read', outcome: 'ok', input: { pattern: 'apps/web/src/features/assistant/**/*.tsx' }, ms: 60, structured: { filenames: ['Thread.tsx', 'Composer.tsx', 'ConversationList.tsx', 'Navigator.tsx', 'ApprovalStep.tsx', 'activity/Activity.tsx', 'activity/templates.tsx', 'activity/orgusViews.tsx', 'AutonomyMenu.tsx'].map((f) => `C:/work/orgus/apps/web/src/features/assistant/${f}`), numFiles: 9 } }) }),
      e('ast_7', at(9), { kind: 'text', runId: run, text: 'Next, what the stream format looks like.' }),
      e('ast_8', at(10), { kind: 'step', runId: run, step: step({ capability: 'tool:WebSearch', title: 'Search the web', tier: 'read', outcome: 'ok', input: { query: 'Claude Code stream-json output format' }, ms: 2_300, result: 'Web search results for query: "Claude Code stream-json output format"\n\nLinks: [{"title":"Run Claude Code programmatically","url":"https://docs.anthropic.com/en/docs/claude-code/headless"},{"title":"CLI reference","url":"https://docs.anthropic.com/en/docs/claude-code/cli-reference"},{"title":"Claude Code overview","url":"https://docs.anthropic.com/en/docs/claude-code/overview"}]' }) }),
      e('ast_9', at(13), { kind: 'step', runId: run, step: step({ capability: 'tool:WebFetch', title: 'Fetch a web page', tier: 'read', outcome: 'ok', input: { url: 'https://docs.anthropic.com/en/docs/claude-code/headless', prompt: 'Summarise stream-json in one line.' }, ms: 3_100, result: 'stream-json prints one JSON object per line: system, assistant, user and result messages, with partial messages when asked.' }) }),
      e('ast_10', at(17), { kind: 'step', runId: run, step: task }),
      e('ast_10a', at(18), { kind: 'step', runId: run, step: step({ capability: 'tool:Glob', title: 'Find files', tier: 'read', outcome: 'ok', parentToolUseId: task.toolUseId, input: { pattern: '**/*.tsx' }, ms: 50, structured: { filenames: ['a.tsx', 'b.tsx'], numFiles: 9 } }) }),
      e('ast_10r', at(26), { kind: 'text', runId: run, parentToolUseId: task.toolUseId, text: 'There are 9 .tsx files.' }),
      e('ast_11', at(30), { kind: 'step', runId: run, step: step({ capability: 'tool:Write', title: 'Write a file', tier: 'elevated', outcome: 'ok', input: { file_path: 'C:/work/orgus/.orgus-dev/tmp/note.md', content: '- Nine views\n- One stream relay\n- One activity model' }, ms: 30 }) }),
      e('ast_12', at(32), { kind: 'step', runId: run, step: step({ capability: 'tool:Edit', title: 'Edit a file', tier: 'elevated', outcome: 'ok', input: { file_path: 'C:/work/orgus/.orgus-dev/tmp/note.md', old_string: '- Nine views', new_string: '- Nine views, one per tool' }, ms: 25 }) }),
      e('ast_13', at(74), {
        kind: 'text',
        runId: run,
        text: 'The chat panel lives in `apps/web/src/features/assistant`.\n\n| What | Where | Count |\n|---|---|---:|\n| Views | features/assistant | 9 |\n| Stream relay | hub ext/assistant | 1 |\n| Docs read | docs.anthropic.com | 1 |',
      }),
    ],
  });
}

export function mockSettings(): AssistantSettings {
  return { ...ASSISTANT_DEFAULT_SETTINGS, mode: 'safe', harness: true, rules: { 'job.run': 'deny', 'project.remove': 'ask', 'tool:Bash': 'deny' } };
}

/** The state slice for these conversations and settings. */
export function stateOf(settings: AssistantSettings, conversations: readonly Conversation[]): AssistantState {
  const pending: AssistantState['pending'] = [];
  for (const c of conversations) {
    for (const en of c.entries) if (en.step?.outcome === 'pending') pending.push({ conversationId: c.id, conversationTitle: c.title, entryId: en.id, step: en.step, at: en.at });
  }
  return {
    settings,
    conversations: [...conversations].sort((a, b) => b.updatedAt - a.updatedAt).map(summarizeConversation),
    pending: pending.sort((a, b) => a.at - b.at),
  };
}

// ---------------------------------------------------------------------------
// Demo answers
// ---------------------------------------------------------------------------

const DEMO_REPLY =
  'This is demo data, so nothing ran. Connected to a hub, I would read the live state through the Orgus tools, act within the autonomy mode, and answer here with what changed and what still needs you.';

/** A new demo conversation for a first message (running until mockReply). */
export function mockStart(text: string, now: number, settings: AssistantSettings, machineId: Id | null): Conversation {
  return conv({
    id: nextId('asc'),
    title: conversationTitle(text),
    state: 'running',
    runId: nextId('run'),
    machineId: settings.machineId ?? machineId,
    model: settings.model,
    createdAt: now,
    updatedAt: now,
    entries: [{ id: nextId('ase'), at: now, kind: 'user', by: 'you', text }],
  });
}

/** The conversation with another message from you, running. */
export function mockSend(c: Conversation, text: string, now: number): Conversation {
  return { ...c, state: 'running', runId: nextId('run'), updatedAt: now, error: null, entries: [...c.entries, { id: nextId('ase'), at: now, kind: 'user', by: 'you', text }] };
}

/** The demo reply lands. */
export function mockReply(c: Conversation, now: number): Conversation {
  if (c.state !== 'running') return c;
  return {
    ...c,
    state: 'idle',
    runId: null,
    updatedAt: now,
    turns: c.turns + 1,
    cost: Math.round((c.cost + 0.02) * 100) / 100,
    entries: [
      ...c.entries,
      { id: nextId('ase'), at: now - 400, kind: 'step', step: step({ capability: 'state.summary', title: 'Summary of everything', tier: 'read', outcome: 'ok', message: 'Read the demo state.', ms: 80 }) },
      { id: nextId('ase'), at: now, kind: 'text', text: DEMO_REPLY },
    ],
  };
}

export function mockStop(c: Conversation, now: number): Conversation {
  const entries = c.entries.map((en) =>
    en.step && (en.step.outcome === 'pending' || en.step.outcome === 'running') ? { ...en, step: { ...en.step, outcome: 'denied' as const, message: 'Stopped by you before it ran.' } } : en,
  );
  return { ...c, state: 'idle', runId: null, updatedAt: now, entries: [...entries, { id: nextId('ase'), at: now, kind: 'note', text: 'You stopped the reply.' }] };
}

/** Answer a pending step: it runs (allow) or is not allowed (deny), and the demo reply follows. */
export function mockDecide(c: Conversation, approvalId: Id, answer: 'allow' | 'deny', note: string | undefined, now: number): Conversation {
  const entries = c.entries.map((en) => {
    const s = en.step;
    if (!s || s.approval?.id !== approvalId || s.outcome !== 'pending') return en;
    const approval = { ...s.approval, decidedBy: 'you', decidedAt: now };
    return answer === 'allow'
      ? { ...en, step: { ...s, approval, outcome: 'ok' as const, message: note ? `Approved by you: ${note}` : 'Approved by you. Done.', ms: 900, auditId: 'au_demo90' } }
      : { ...en, step: { ...s, approval, outcome: 'denied' as const, message: note ? `Denied by you: ${note}` : 'Denied by you.' } };
  });
  const text =
    answer === 'allow'
      ? 'The worktree is removed. Session `91c2` is still running on its machine; I will leave it until the machine answers again.'
      : 'Understood, I left the worktree in place. Session `91c2` is still running on its machine.';
  return { ...c, state: 'idle', runId: null, updatedAt: now, turns: c.turns + 1, entries: [...entries, { id: nextId('ase'), at: now, kind: 'text', text }] };
}
